import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence } from '../../common/ViewingTemplate';
import PdfMarkupViewer from '../../Technical/Sheets/PdfMarkupViewer';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const TAB_TRENDS = 'trends';
const TAB_SHEET = 'sheet';

const ProgressTrendsModal = React.memo(({ isOpen, onClose, matrixId, lineTagNumber, projectId }) => {
  const { instance, accounts } = useMsal();
  const [shopTrends, setShopTrends] = useState([]);
  const [fieldTrends, setFieldTrends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_TRENDS);
  const [matchedSheet, setMatchedSheet] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  // Same presence room as Technical → Sheets so viewers see each other either place
  const sheetPresenceEnabled = isOpen && activeTab === TAB_SHEET && !!matchedSheet?.id;
  const { viewers, isConnected, connectionState } = usePresence(
    'sheet',
    matchedSheet ? String(matchedSheet.id) : 'none',
    sheetPresenceEnabled
  );

  useEffect(() => {
    if (isOpen && matrixId) {
      fetchProgressTrends();
    }
  }, [isOpen, matrixId]);

  useEffect(() => {
    if (isOpen && projectId && lineTagNumber) {
      lookupMatchingSheet();
    }
  }, [isOpen, projectId, lineTagNumber]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab(TAB_TRENDS);
      setMatchedSheet(null);
      setPdfError(null);
      setError(null);
      setShopTrends([]);
      setFieldTrends([]);
      setPdfUrl(null);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const fetchProgressTrends = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/matrix/trends/${matrixId}`
      );

      if (response.data.success) {
        const data = response.data.data;
        setShopTrends(data.shopTrends || []);
        setFieldTrends(data.fieldTrends || []);
      } else {
        setError(response.data.message || 'Failed to fetch progress trends');
      }
    } catch (error) {
      console.error('Error fetching progress trends:', error);
      setError('Error fetching progress trends: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const lookupMatchingSheet = async () => {
    try {
      setSheetLoading(true);
      const encoded = encodeURIComponent(lineTagNumber);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/technical-sheets/projects/${projectId}/sheets/lookup/${encoded}`
      );

      if (response.data.success && response.data.matched && response.data.sheets.length > 0) {
        setMatchedSheet(response.data.sheets[0]);
      } else {
        setMatchedSheet(null);
      }
    } catch (err) {
      console.error('Error looking up matching sheet:', err);
      setMatchedSheet(null);
    } finally {
      setSheetLoading(false);
    }
  };

  const loadPdf = async (sheetId) => {
    try {
      setPdfError(null);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/technical-sheets/sheets/${sheetId}/file`,
        null,
        { responseType: 'blob' }
      );

      const blob = response.data;
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error('Error loading PDF:', err);
      setPdfError('Failed to load PDF. The file may have been removed.');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === TAB_SHEET && matchedSheet && !pdfUrl) {
      loadPdf(matchedSheet.id);
    }
  };

  const handleCloseSheetViewer = () => {
    setActiveTab(TAB_TRENDS);
  };

  const sortedShopTrends = useMemo(
    () => [...(shopTrends || [])].sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at)),
    [shopTrends]
  );

  const sortedFieldTrends = useMemo(
    () => [...(fieldTrends || [])].sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at)),
    [fieldTrends]
  );

  const hasAnyTrends = shopTrends.length > 0 || fieldTrends.length > 0;

  const prepareChartData = () => {
    if (!hasAnyTrends) {
      return { labels: [], datasets: [] };
    }

    const datasets = [];

    if (sortedShopTrends.length > 0) {
      datasets.push({
        label: 'Shop Progress (%)',
        data: sortedShopTrends.map(trend => ({ x: new Date(trend.changed_at), y: trend.new_progress })),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(75, 192, 192)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 8,
        fill: false,
        tension: 0.1
      });
    }

    if (sortedFieldTrends.length > 0) {
      datasets.push({
        label: 'Field Progress (%)',
        data: sortedFieldTrends.map(trend => ({ x: new Date(trend.changed_at), y: trend.new_progress })),
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.2)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(255, 159, 64)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 8,
        fill: false,
        tension: 0.1
      });
    }

    return { datasets };
  };

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    clip: false,
    layout: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: `Progress Trends - Line Tag: ${lineTagNumber}`,
        font: { size: 16, weight: 'bold' }
      },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        callbacks: {
          title: (context) => new Date(context[0].parsed.x).toLocaleString(),
          label: (context) => {
            const datasetLabel = context.dataset.label || '';
            return `${datasetLabel}: ${Number(context.parsed.y).toFixed(1)}%`;
          },
          afterLabel: (context) => {
            const datasetIndex = context.datasetIndex;
            const dataIndex = context.dataIndex;
            const trends = datasetIndex === 0 && sortedShopTrends.length > 0
              ? sortedShopTrends
              : sortedFieldTrends;
            const trend = trends[dataIndex];
            return trend?.user_name ? `Updated by: ${trend.user_name}` : '';
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: { displayFormats: { day: 'MMM dd', hour: 'MMM dd, HH:mm' } },
        title: { display: true, text: 'Date & Time' }
      },
      y: {
        beginAtZero: true,
        max: 100,
        min: 0,
        title: { display: true, text: 'Progress (%)' },
        ticks: { callback: (value) => value + '%', stepSize: 10 },
        grid: { drawBorder: true, display: true }
      }
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false }
  }), [lineTagNumber, sortedShopTrends, sortedFieldTrends]);

  if (!isOpen) return null;

  const hasSheet = !!matchedSheet;

  const tabStyle = (isActive) => ({
    padding: '8px 20px',
    border: 'none',
    borderBottom: isActive ? '3px solid #007bff' : '3px solid transparent',
    background: 'none',
    color: isActive ? '#007bff' : '#6c757d',
    fontWeight: isActive ? '600' : '400',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  });

  const sheetViewerOpen = activeTab === TAB_SHEET && !!pdfUrl && !!matchedSheet;

  return (
    <>
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: sheetViewerOpen ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '1.5rem 2rem',
        width: 'min(1400px, 96vw)',
        height: 'min(920px, 92vh)',
        maxWidth: '96vw',
        maxHeight: '92vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem',
          borderBottom: '1px solid #e9ecef',
          paddingBottom: '0.75rem'
        }}>
          <h2 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
            {lineTagNumber}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0.25rem'
            }}
          >
            &times;
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid #e9ecef',
          marginBottom: '1.5rem'
        }}>
          <button style={tabStyle(activeTab === TAB_TRENDS)} onClick={() => handleTabChange(TAB_TRENDS)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            Progress Trends
          </button>
          <button
            style={{
              ...tabStyle(activeTab === TAB_SHEET),
              opacity: hasSheet || sheetLoading ? 1 : 0.45,
              cursor: hasSheet ? 'pointer' : 'default'
            }}
            onClick={() => hasSheet && handleTabChange(TAB_SHEET)}
            disabled={!hasSheet}
            title={
              sheetLoading
                ? 'Checking for matching sheet...'
                : hasSheet
                  ? `Open sheet & markup: ${matchedSheet.number}`
                  : 'No matching sheet found in Technical Sheets'
            }
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            Sheet & Markup
            {sheetLoading && (
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                border: '2px solid #dee2e6',
                borderTop: '2px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
            )}
            {hasSheet && !sheetLoading && (
              <span style={{
                backgroundColor: '#28a745',
                color: 'white',
                fontSize: '10px',
                padding: '1px 6px',
                borderRadius: '10px',
                fontWeight: '600'
              }}>
                PDF
              </span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div style={{ minHeight: '400px', flex: 1 }}>
          {/* ===== TRENDS TAB ===== */}
          {activeTab === TAB_TRENDS && (
            <>
              {loading && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #007bff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <span style={{ color: '#666' }}>Loading progress trends...</span>
                </div>
              )}

              {error && (
                <div style={{
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  padding: '1rem',
                  borderRadius: '4px',
                  border: '1px solid #f5c6cb',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}

              {!loading && !error && !hasAnyTrends && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#6c757d'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#adb5bd" strokeWidth="1.5">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  </div>
                  <h3>No Progress Trends Found</h3>
                  <p>This line tag doesn't have any progress update history yet.</p>
                </div>
              )}

              {!loading && !error && hasAnyTrends && (
                <>
                  <div style={{ 
                    height: '400px', 
                    marginBottom: '2rem',
                    padding: '20px 15px 10px 15px',
                    overflow: 'visible'
                  }}>
                    <Line data={prepareChartData()} options={chartOptions} />
                  </div>

                  {shopTrends.length > 0 && (
                    <div>
                      <h3 style={{ marginBottom: '1rem', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: 'rgb(75, 192, 192)', borderRadius: '50%' }} />
                        Shop Progress History
                      </h3>
                      <div style={{
                        maxHeight: '250px',
                        overflow: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        marginBottom: '1.5rem'
                      }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '14px'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                              {['Date & Time', 'Previous Progress', 'New Progress', 'Change', 'Updated By'].map((h, i) => (
                                <th key={h} style={{
                                  padding: '12px',
                                  borderBottom: '1px solid #dee2e6',
                                  textAlign: i === 0 || i === 4 ? 'left' : 'center',
                                  fontWeight: 'bold',
                                  position: 'sticky',
                                  top: 0,
                                  backgroundColor: '#f8f9fa'
                                }}>
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {[...shopTrends]
                              .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at))
                              .map((trend, index) => {
                                const change = trend.new_progress - (trend.previous_progress || 0);
                                const changeColor = change > 0 ? '#28a745' : change < 0 ? '#dc3545' : '#6c757d';
                                
                                return (
                                  <tr key={trend.id || index} style={{
                                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                                  }}>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6' }}>
                                      {new Date(trend.changed_at).toLocaleString()}
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                                      {trend.previous_progress || 0}%
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', textAlign: 'center', fontWeight: 'bold' }}>
                                      {trend.new_progress}%
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', textAlign: 'center', color: changeColor, fontWeight: 'bold' }}>
                                      {change > 0 ? '+' : ''}{Number(change).toFixed(1)}%
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6' }}>
                                      {trend.user_name || 'System'}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {fieldTrends.length > 0 && (
                    <div>
                      <h3 style={{ marginBottom: '1rem', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: 'rgb(255, 159, 64)', borderRadius: '50%' }} />
                        Field Progress History
                      </h3>
                      <div style={{
                        maxHeight: '250px',
                        overflow: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px'
                      }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '14px'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                              {['Date & Time', 'Previous Progress', 'New Progress', 'Change', 'Updated By'].map((h, i) => (
                                <th key={h} style={{
                                  padding: '12px',
                                  borderBottom: '1px solid #dee2e6',
                                  textAlign: i === 0 || i === 4 ? 'left' : 'center',
                                  fontWeight: 'bold',
                                  position: 'sticky',
                                  top: 0,
                                  backgroundColor: '#f8f9fa'
                                }}>
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {[...fieldTrends]
                              .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at))
                              .map((trend, index) => {
                                const change = trend.new_progress - (trend.previous_progress || 0);
                                const changeColor = change > 0 ? '#28a745' : change < 0 ? '#dc3545' : '#6c757d';
                                
                                return (
                                  <tr key={trend.id || index} style={{
                                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                                  }}>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6' }}>
                                      {new Date(trend.changed_at).toLocaleString()}
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                                      {Number(trend.previous_progress || 0).toFixed(1)}%
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', textAlign: 'center', fontWeight: 'bold' }}>
                                      {Number(trend.new_progress).toFixed(1)}%
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', textAlign: 'center', color: changeColor, fontWeight: 'bold' }}>
                                      {change > 0 ? '+' : ''}{Number(change).toFixed(1)}%
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6' }}>
                                      {trend.user_name || 'System'}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ===== SHEET & MARKUP TAB (loading / error only — full viewer is overlay below) ===== */}
          {activeTab === TAB_SHEET && !pdfUrl && (
            <div>
              {pdfError && (
                <div style={{
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  padding: '1rem',
                  borderRadius: '4px',
                  border: '1px solid #f5c6cb',
                  textAlign: 'center'
                }}>
                  {pdfError}
                </div>
              )}

              {!pdfError && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '500px',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #007bff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <span style={{ color: '#666' }}>Loading sheet for markup…</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '2rem',
          paddingTop: '1rem',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {!hasSheet && !sheetLoading && activeTab === TAB_TRENDS && (
            <span style={{ fontSize: '12px', color: '#adb5bd' }}>
              No matching PDF found in Technical Sheets for this ISO number.
            </span>
          )}
          {(hasSheet || sheetLoading || activeTab === TAB_SHEET) && <span />}
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>

    {/* Portal to body so toolbar isn't clipped by project layout / modal bounds */}
    {sheetViewerOpen && createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10050,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#f5f7fa',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <PdfMarkupViewer
            pdfUrl={pdfUrl}
            sheetId={matchedSheet.id}
            sheetTitle={[matchedSheet.number, matchedSheet.version_set].filter(Boolean).join(' ') || lineTagNumber}
            onClose={handleCloseSheetViewer}
            projectId={projectId}
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
          />
        </div>
      </div>,
      document.body
    )}
    </>
  );
});

export default ProgressTrendsModal;
